export type CancellationReasonCategory =
  | "operational_issue"
  | "emergency"
  | "scheduling_error"
  | "no_longer_needed"
  | "severe_weather"
  | "other";

export interface BookingApplicationDate {
  date?: string | null;
  hour?: string | null;
}

export const CANCELLATION_REASON_OPTIONS: Array<{
  value: CancellationReasonCategory;
  label: string;
}> = [
  { value: "operational_issue", label: "Operational issue" },
  { value: "emergency", label: "Emergency" },
  { value: "scheduling_error", label: "Scheduling error" },
  { value: "no_longer_needed", label: "No longer needed" },
  { value: "severe_weather", label: "Severe weather" },
  { value: "other", label: "Other" },
];

const parseHour = (
  hourRaw?: string | null,
): { hour: number; minute: number } | null => {
  if (!hourRaw) return null;
  const normalized = hourRaw.trim();
  if (!normalized) return null;

  const amPmMatch = normalized.match(/^(\d{1,2})(?::(\d{2}))?\s*(AM|PM)$/i);
  if (amPmMatch) {
    let hour = Number(amPmMatch[1]);
    const minute = Number(amPmMatch[2] || "0");
    const period = amPmMatch[3].toUpperCase();

    if (Number.isNaN(hour) || Number.isNaN(minute) || minute > 59) return null;

    if (hour === 12) {
      hour = period === "AM" ? 0 : 12;
    } else if (period === "PM") {
      hour += 12;
    }

    if (hour < 0 || hour > 23) return null;

    return { hour, minute };
  }

  const twentyFourMatch = normalized.match(/^(\d{1,2})(?::(\d{2}))?$/);
  if (twentyFourMatch) {
    const hour = Number(twentyFourMatch[1]);
    const minute = Number(twentyFourMatch[2] || "0");

    if (Number.isNaN(hour) || Number.isNaN(minute)) return null;
    if (hour < 0 || hour > 23 || minute < 0 || minute > 59) return null;

    return { hour, minute };
  }

  return null;
};

const parseDateParts = (
  dateRaw?: string | null,
): { year: number; monthIndex: number; day: number } | null => {
  if (!dateRaw) return null;
  const normalized = dateRaw.trim();
  if (!normalized) return null;

  // Keep the calendar date as provided (YYYY-MM-DD), independent from timezone offsets.
  const dateMatch = normalized.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (dateMatch) {
    const year = Number(dateMatch[1]);
    const month = Number(dateMatch[2]);
    const day = Number(dateMatch[3]);
    if (
      Number.isNaN(year) ||
      Number.isNaN(month) ||
      Number.isNaN(day) ||
      month < 1 ||
      month > 12 ||
      day < 1 ||
      day > 31
    ) {
      return null;
    }

    return { year, monthIndex: month - 1, day };
  }

  const parsed = new Date(normalized);
  if (Number.isNaN(parsed.getTime())) return null;

  return {
    year: parsed.getFullYear(),
    monthIndex: parsed.getMonth(),
    day: parsed.getDate(),
  };
};

export const parseBookingStartAt = (
  applicationDate?: BookingApplicationDate,
): Date | null => {
  if (!applicationDate?.date) return null;

  const parsedHour = parseHour(applicationDate.hour);

  // If full datetime is provided and no separate hour exists, keep the original timestamp.
  if (!parsedHour && applicationDate.date.includes("T")) {
    const fullDate = new Date(applicationDate.date);
    if (!Number.isNaN(fullDate.getTime())) return fullDate;
  }

  const dateParts = parseDateParts(applicationDate.date);
  if (!dateParts) return null;

  const startAt = new Date(
    dateParts.year,
    dateParts.monthIndex,
    dateParts.day,
    parsedHour?.hour ?? 0,
    parsedHour?.minute ?? 0,
    0,
    0,
  );

  if (Number.isNaN(startAt.getTime())) return null;

  return startAt;
};

export const canCancelBooking = (
  startAt: Date | null,
  now: Date = new Date(),
): boolean => {
  if (!startAt || Number.isNaN(startAt.getTime())) return false;
  return startAt.getTime() > now.getTime();
};

export const canCancelBookingFromApplicationDate = (
  applicationDate?: BookingApplicationDate,
  now: Date = new Date(),
): boolean => {
  const startAt = parseBookingStartAt(applicationDate);
  return canCancelBooking(startAt, now);
};

export const getTimeBeforeEventMinutes = (
  startAt: Date,
  now: Date = new Date(),
): number => {
  return Math.floor((startAt.getTime() - now.getTime()) / (1000 * 60));
};

export const isLateCancellation = (
  startAt: Date,
  thresholdMinutes = 240,
  now: Date = new Date(),
): boolean => {
  const diff = getTimeBeforeEventMinutes(startAt, now);
  return diff >= 0 && diff < thresholdMinutes;
};

export const getCancellationTiming = (
  applicationDate?: BookingApplicationDate,
  thresholdMinutes = 240,
  now: Date = new Date(),
) => {
  const startAt = parseBookingStartAt(applicationDate);
  if (!startAt) return null;

  const timeBeforeEventMinutes = getTimeBeforeEventMinutes(startAt, now);

  return {
    startAt,
    timeBeforeEventMinutes,
    isLastMinute: isLateCancellation(startAt, thresholdMinutes, now),
  };
};
